/* Hifz — memorising the Qurʾān, and keeping it. Pure (unit-tested); the store
   (src/lib/store.ts) only saves what these functions return.

   The day follows the way ḥuffāẓ have long been taught (the madrasa's three
   tracks, used from South Asia to Indonesia):
     sabaq   the new lesson — sized so you finish by your target date;
     sabqi   recent pages — every page you learn is revised daily for a week;
     manzil  older pages — revised on a spaced schedule: a page you know well
             comes back less often (never more than a month apart), a page you
             stumbled on comes back sooner, and one you forgot goes back to
             daily revision and is flagged as needing work.
   The unit is the Madinah muṣḥaf page (the 1421 print, 604 pages) — the page
   ḥuffāẓ picture as they recite. "Lines" are pages × 15. */
import pages from '@/data/pages.json';
import { CHAPTERS } from '@/features/quran/api';
import { addDays } from '@shared/prayer';
import type { Stamped } from '@/lib/types';

/** Order of memorising: from an-Nās back to al-Fātiḥah (surah by surah, each read forward);
    Juz ʿAmma first, then from al-Baqarah onward; or from al-Fātiḥah to an-Nās. */
export type HifzOrder = 'end' | 'amma-first' | 'start';
export type Grade = 'strong' | 'shaky' | 'forgot';

export interface HifzPlan extends Stamped {
  id: 'main';
  order: HifzOrder;
  startDay: string;
  /** finish by (null: at your own pace) */
  targetDay: string | null;
  /** pages per study day without a target date */
  pace: number;
  /** weekdays with a new lesson, 0 = Sunday … 6 = Saturday (revision is every day) */
  studyDays: number[];
  /** surahs already memorised when the plan began (or added later) */
  known: number[];
  /** learn the passages the Sunnah singles out first (PASSAGES), then the rest in `order` */
  sunnahFirst?: boolean;
  /** āyāt of the learning order memorised so far */
  position: number;
  /** where the current goal was set (pace is measured from here, so changing the goal starts it afresh) */
  startPosition?: number;
  /** āyah ids marked "not memorised" after all, from before `position` — learnt again before new lessons */
  relearn?: number[];
  /** most pages of older revision (manzil) to give in a day; the rest wait for the next day */
  maxReview: number;
  createdAt: number;
}

/** One memorised page's revision state (id = the page number). */
export interface HifzPage extends Stamped {
  id: string;
  page: number;
  learnedOn: string;
  /** revised daily (sabqi) up to and including this day */
  recentUntil: string;
  /** next revision once it is past its first week (manzil) */
  due: string;
  /** days to the next manzil revision */
  interval: number;
  /** how quickly the interval grows (1.3 – 2.8) */
  ease: number;
  lapses: number;
  reviews: number;
  last?: { day: string; grade: Grade };
}

export interface HifzDay extends Stamped { dayKey: string; newAyat: number; reviewed: number }

/** A saved lesson (id = its first āyah id and the day), so it can be marked "not memorised" if saved too soon. */
export interface HifzLessonRec extends Stamped { id: string; day: string; ids: number[] }

/* ── The muṣḥaf: āyah ids (1–6236), pages, weights ─────────────────── */
const START: number[] = pages.v2;
export const PAGE_COUNT = 604;
const FIRST_ID: number[] = [0];
CHAPTERS.forEach((_, i) => { FIRST_ID[i + 1] = i ? FIRST_ID[i] + CHAPTERS[i - 1].v : 1; });

/** Page (1–604) of an āyah id. */
export function pageOfId(id: number): number {
  let lo = 0, hi = START.length - 1;
  while (lo < hi) { const mid = (lo + hi + 1) >> 1; if (START[mid] <= id) lo = mid; else hi = mid - 1; }
  return lo + 1;
}
export const pageIds = (p: number): [number, number] => [START[p - 1], p < PAGE_COUNT ? START[p] - 1 : 6236];
export const surahOfId = (id: number) => { let s = 1; while (s < 114 && FIRST_ID[s + 1] <= id) s++; return s; };
export const keyOfId = (id: number) => { const s = surahOfId(id); return `${s}:${id - FIRST_ID[s] + 1}`; };
export const surahIds = (s: number): [number, number] => [FIRST_ID[s], FIRST_ID[s] + CHAPTERS[s - 1].v - 1];

/** Each āyah's share of its page (a page's āyāt add up to 1), so lessons are sized in pages. */
const WEIGHT = new Float64Array(6237);
for (let p = 1; p <= PAGE_COUNT; p++) { const [a, b] = pageIds(p); for (let id = a; id <= b; id++) WEIGHT[id] = 1 / (b - a + 1); }
export const weightOf = (id: number) => WEIGHT[id];

/** The passages the Sunnah singles out, in the order they're learnt when "Sunnah passages first" is on.
    The hadith for each are in virtues.json (hifz.passages), quoted on the guide page. */
export const PASSAGES: { id: string; title: string; why: string; ranges: [surah: number, from: number, to: number][] }[] = [
  { id: 'fatihah', title: 'Al-Fātiḥah', why: 'The greatest surah in the Qurʾān, recited in every rakʿah', ranges: [[1, 1, 7]] },
  { id: 'ikhlas', title: 'Al-Ikhlāṣ', why: 'Equal to a third of the Qurʾān', ranges: [[112, 1, 4]] },
  { id: 'muawwidhatayn', title: 'Al-Falaq & an-Nās', why: 'Seeking refuge — none like them was ever revealed', ranges: [[113, 1, 5], [114, 1, 6]] },
  { id: 'kursi', title: 'Āyat al-Kursī', why: 'The greatest āyah; protection until morning', ranges: [[2, 255, 255]] },
  { id: 'baqarah-end', title: 'The last two āyāt of al-Baqarah', why: 'Recited at night, they suffice', ranges: [[2, 285, 286]] },
  { id: 'kahf-10', title: 'The first ten āyāt of al-Kahf', why: 'Protection from the Dajjāl', ranges: [[18, 1, 10]] },
  { id: 'mulk', title: 'Al-Mulk', why: 'Intercedes for its reader until he is forgiven', ranges: [[67, 1, 30]] },
  { id: 'imran-end', title: 'The end of Āl ʿImrān', why: 'What the Prophet ﷺ recited on waking for the night prayer', ranges: [[3, 190, 200]] },
];
const passageIds = (known: ReadonlySet<number>) => PASSAGES.flatMap((p) => p.ranges.filter(([s]) => !known.has(s)).flatMap(([s, a, b]) => range(surahIds(s)[0] + a - 1, surahIds(s)[0] + b - 1)));
const PASSAGE_OF = new Map<number, (typeof PASSAGES)[number]>();
for (const p of PASSAGES) for (const [s, a, b] of p.ranges) for (let ay = a; ay <= b; ay++) PASSAGE_OF.set(surahIds(s)[0] + ay - 1, p);
/** The Sunnah passage an āyah belongs to, if any. */
export const passageOfId = (id: number) => PASSAGE_OF.get(id) ?? null;

const seqCache = new Map<string, number[]>();
/** The āyah ids still to learn, in learning order (known surahs left out). With `sunnahFirst`, the Sunnah
    passages come first and the rest follow in `order`, without them. */
export function sequence(order: HifzOrder, known: readonly number[], sunnahFirst = false): number[] {
  const k = `${order}|${sunnahFirst ? 1 : 0}|${[...known].sort((a, b) => a - b).join(',')}`;
  const hit = seqCache.get(k);
  if (hit) return hit;
  const skip = new Set(known);
  const surahs = order === 'start' ? range(1, 114)
    : order === 'end' ? range(1, 114).reverse()
    : [...range(78, 114).reverse(), ...range(1, 77)];   // Juz ʿAmma (an-Nabaʾ – an-Nās) from the end, then from al-Fātiḥah
  const first = sunnahFirst ? passageIds(skip) : [];
  const taken = new Set(first);
  const out: number[] = [...first];
  for (const s of surahs) { if (skip.has(s)) continue; const [a, b] = surahIds(s); for (let id = a; id <= b; id++) if (!taken.has(id)) out.push(id); }
  seqCache.set(k, out);
  return out;
}
/** The plan's learning order. */
export const planSequence = (p: Pick<HifzPlan, 'order' | 'known' | 'sunnahFirst'>) => sequence(p.order, p.known, !!p.sunnahFirst);
const range = (a: number, b: number) => Array.from({ length: b - a + 1 }, (_, i) => a + i);

/** Every memorised āyah id: the known surahs and the learnt part of the order. */
export function memorisedIds(plan: Pick<HifzPlan, 'order' | 'known' | 'position' | 'sunnahFirst' | 'relearn'>, withRelearn = false): Set<number> {
  const out = new Set<number>();
  for (const s of plan.known) { const [a, b] = surahIds(s); for (let id = a; id <= b; id++) out.add(id); }
  for (const id of planSequence(plan).slice(0, plan.position)) out.add(id);
  if (!withRelearn) for (const id of plan.relearn ?? []) out.delete(id);
  return out;
}

const relearnWeight = (plan: Pick<HifzPlan, 'relearn'>) => (plan.relearn ?? []).reduce((a, id) => a + WEIGHT[id], 0);

/** The next lesson: āyāt marked "not memorised" come first, as they were (a run at a time, in learning order);
    otherwise the next `size` pages from where you are. */
export function nextLesson(plan: HifzPlan, size: number): Lesson | null {
  const again = new Set(plan.relearn ?? []);
  if (again.size) {
    const seq = planSequence(plan);
    let from = 0;
    while (from < plan.position && !again.has(seq[from])) from++;
    if (from < plan.position) {
      let to = from;
      while (to < plan.position && again.has(seq[to])) to++;
      const parts: Lesson['parts'] = [];
      let pages = 0;
      for (let i = from; i < to; i++) {
        const id = seq[i], s = surahOfId(id), a = id - FIRST_ID[s] + 1, last = parts.at(-1);
        pages += WEIGHT[id];
        if (last && last.surah === s && last.to === a - 1) last.to = a; else parts.push({ surah: s, from: a, to: a });
      }
      return { from, to, parts, pages, relearn: true };
    }
  }
  return lessonAt(plan, plan.position, size);
}

/** Pages memorised (fractions count: half a page learnt is 0.5). */
export function pagesMemorised(plan: Pick<HifzPlan, 'order' | 'known' | 'position' | 'sunnahFirst' | 'relearn'>): number {
  let n = 0;
  for (const id of memorisedIds(plan)) n += WEIGHT[id];
  return n;
}

/** Memorised fraction of each juz (1–30), for the progress grid. */
export function juzProgress(plan: Pick<HifzPlan, 'order' | 'known' | 'position' | 'sunnahFirst' | 'relearn'>, juzFirstIds: number[]): number[] {
  const mem = memorisedIds(plan);
  return juzFirstIds.map((a, j) => {
    const b = j < 29 ? juzFirstIds[j + 1] - 1 : 6236;
    let got = 0, all = 0;
    for (let id = a; id <= b; id++) { all += WEIGHT[id]; if (mem.has(id)) got += WEIGHT[id]; }
    return all ? got / all : 0;
  });
}

/* ── Pace ─────────────────────────────────────────────────────────── */
const weekday = (day: string) => new Date(`${day}T12:00:00Z`).getUTCDay();
export const isStudyDay = (plan: Pick<HifzPlan, 'studyDays'>, day: string) => plan.studyDays.includes(weekday(day));

/** Study days from `from` to `to`, both included. */
export function studyDaysBetween(studyDays: readonly number[], from: string, to: string): number {
  if (to < from || !studyDays.length) return 0;
  const days = Math.round((Date.parse(`${to}T12:00:00Z`) - Date.parse(`${from}T12:00:00Z`)) / 864e5) + 1;
  const full = Math.floor(days / 7);
  let n = full * studyDays.length;
  for (let i = full * 7; i < days; i++) if (studyDays.includes(weekday(addDays(from, i)))) n++;
  return n;
}

export const remainingPages = (plan: HifzPlan, position = plan.position) => {
  const seq = planSequence(plan);
  let n = relearnWeight(plan);
  for (let i = position; i < seq.length; i++) n += WEIGHT[seq[i]];
  return n;
};

/** Pages to learn per study day: to finish by the target date (counting from the start of today), or your own pace. */
export function dailyPages(plan: HifzPlan, today: string, positionAtStartOfDay = plan.position): number {
  if (!plan.targetDay) return plan.pace;
  const left = studyDaysBetween(plan.studyDays, today, plan.targetDay);
  const rest = remainingPages(plan, positionAtStartOfDay);
  return left > 0 ? rest / left : rest;
}

export interface Lesson {
  /** positions in the learning order, [from, to) */
  from: number;
  to: number;
  /** one per surah the lesson touches: { surah, fromAyah, toAyah } */
  parts: { surah: number; from: number; to: number }[];
  pages: number;
  /** āyāt you marked "not memorised", being learnt again */
  relearn?: boolean;
}

/** The lesson starting at `position`, about `size` pages. It never splits an āyah, and stops at the end of a surah
    once it has most of the day's portion (so a lesson doesn't begin a surah with a fragment). */
export function lessonAt(plan: Pick<HifzPlan, 'order' | 'known' | 'sunnahFirst'>, position: number, size: number): Lesson | null {
  const seq = planSequence(plan);
  if (position >= seq.length) return null;
  let to = position, got = 0;
  while (to < seq.length) {
    const id = seq[to];
    if (to > position && got >= size * 0.6 && (surahOfId(id) !== surahOfId(seq[to - 1]) || id !== seq[to - 1] + 1)) break;   // a surah or passage ends, with enough learnt
    got += WEIGHT[id];
    to++;
    if (got >= size) {
      // Finish a short tail of the surah (or passage) rather than leave a few āyāt for tomorrow.
      const s = surahOfId(id);
      let tail = 0, t = to;
      while (t < seq.length && surahOfId(seq[t]) === s && seq[t] === seq[t - 1] + 1) tail += WEIGHT[seq[t++]];
      // A Sunnah passage (e.g. the last two āyāt of al-Baqarah) is learnt whole when up to half a lesson of it is left.
      const inPassage = !!PASSAGE_OF.get(id) && PASSAGE_OF.get(id) === PASSAGE_OF.get(seq[to]);
      if (tail > 0 && tail <= size * (inPassage ? 0.5 : 0.25)) { to = t; got += tail; }
      break;
    }
  }
  const parts: Lesson['parts'] = [];
  for (let i = position; i < to; i++) {
    const id = seq[i], s = surahOfId(id), a = id - FIRST_ID[s] + 1;
    const last = parts.at(-1);
    if (last && last.surah === s && last.to === a - 1) last.to = a;
    else parts.push({ surah: s, from: a, to: a });
  }
  return { from: position, to, parts, pages: got };
}

/** Where you'd be on a steady pace by the start of today, and how many study days behind (0 if on track). */
export function paceStatus(plan: HifzPlan, today: string): { expectedPages: number; learntPages: number; behindDays: number; onTrack: boolean } {
  const seq = planSequence(plan);
  const from = Math.min(plan.startPosition ?? 0, plan.position);
  let learnt = 0, total = 0;
  for (let i = from; i < seq.length; i++) { total += WEIGHT[seq[i]]; if (i < plan.position) learnt += WEIGHT[seq[i]]; }
  learnt = Math.max(0, learnt - relearnWeight(plan));
  if (!plan.targetDay) return { expectedPages: learnt, learntPages: learnt, behindDays: 0, onTrack: true };
  const all = studyDaysBetween(plan.studyDays, plan.startDay, plan.targetDay);
  const done = studyDaysBetween(plan.studyDays, plan.startDay, addDays(today, -1));
  const expected = all ? Math.min(total, (total * done) / all) : total;
  const perDay = all ? total / all : total;
  const behindDays = Math.max(0, Math.floor((expected - learnt) / perDay + 1e-9));
  return { expectedPages: expected, learntPages: learnt, behindDays, onTrack: behindDays === 0 };
}

/** After the known surahs (or the order) change: how much of the new learning order is already memorised.
    Learnt āyāt stay learnt as long as they still begin the order; a surah taken off the known list is to learn again. */
export function repositionFor(old: Pick<HifzPlan, 'order' | 'known' | 'position' | 'sunnahFirst' | 'relearn'>, order: HifzOrder, known: readonly number[], sunnahFirst = !!old.sunnahFirst): number {
  const mem = memorisedIds(old, true);
  const seq = sequence(order, known, sunnahFirst);
  let n = 0;
  while (n < seq.length && mem.has(seq[n])) n++;
  return n;
}

/* ── Revision ─────────────────────────────────────────────────────── */
export const RECENT_DAYS = 7;      // sabqi: daily for the first week
export const MAX_INTERVAL = 30;    // manzil: every page at least once a month
const START_EASE = 2.3, MIN_EASE = 1.3, MAX_EASE = 2.8;

const fresh = (page: number, today: string): HifzPage => ({
  id: String(page), page, learnedOn: today, recentUntil: addDays(today, RECENT_DAYS - 1), due: addDays(today, RECENT_DAYS),
  interval: 3, ease: START_EASE, lapses: 0, reviews: 0, updatedAt: Date.now(),
});

/** Pages touched by a lesson just learnt: new ones start their week of daily revision; a page you
    were already part-way through starts its week again (it has new āyāt in it). */
export function afterLesson(existing: Record<string, HifzPage>, ids: readonly number[], today: string): HifzPage[] {
  const touched = [...new Set(ids.map(pageOfId))];
  return touched.map((p) => {
    const prev = existing[String(p)];
    if (!prev || prev.deleted) return fresh(p, today);
    return { ...prev, recentUntil: addDays(today, RECENT_DAYS - 1), due: addDays(today, RECENT_DAYS), updatedAt: Date.now() };
  });
}

/** Pages of surahs you already know: straight into older revision, spread over the next two weeks (not all on day one). */
export function seedKnown(existing: Record<string, HifzPage>, surahs: readonly number[], today: string): HifzPage[] {
  const ps = new Set<number>();
  for (const s of surahs) { const [a, b] = surahIds(s); for (let id = a; id <= b; id++) ps.add(pageOfId(id)); }
  const fresh = [...ps].filter((p) => !existing[String(p)] || existing[String(p)].deleted).sort((a, b) => a - b);
  const spread = Math.min(14, Math.max(1, fresh.length));
  return fresh.map((p, i) => ({
    id: String(p), page: p, learnedOn: today, recentUntil: addDays(today, -1), due: addDays(today, (i % spread) + 1),
    interval: 7, ease: START_EASE, lapses: 0, reviews: 0, updatedAt: Date.now(),
  }));
}

export const isRecent = (p: HifzPage, today: string) => p.recentUntil >= today;
export const needsWork = (p: HifzPage) => !p.deleted && (p.last?.grade === 'forgot' || p.last?.grade === 'shaky' || p.ease < 1.9);
const revisedToday = (p: HifzPage, today: string) => p.last?.day === today;

export interface Queue {
  /** sabqi: learnt this week, revised every day */
  recent: HifzPage[];
  /** manzil: due today, most overdue and weakest first, up to the day's limit */
  due: HifzPage[];
  /** due, but past the day's limit — they come first tomorrow */
  waiting: number;
  /** already revised today */
  done: HifzPage[];
}

export function reviewQueue(all: Record<string, HifzPage>, today: string, maxReview: number): Queue {
  const live = Object.values(all).filter((p) => !p.deleted);
  const done = live.filter((p) => revisedToday(p, today));
  const open = live.filter((p) => !revisedToday(p, today));
  const recent = open.filter((p) => isRecent(p, today)).sort((a, b) => b.learnedOn.localeCompare(a.learnedOn) || a.page - b.page);
  const dueAll = open.filter((p) => !isRecent(p, today) && p.due <= today)
    .sort((a, b) => a.due.localeCompare(b.due) || a.ease - b.ease || a.page - b.page);
  const room = Math.max(0, maxReview - done.filter((p) => !isRecent(p, today)).length);
  return { recent, due: dueAll.slice(0, room), waiting: Math.max(0, dueAll.length - room), done };
}

/** Record a revision. In the first week a page stays daily (a forgotten one gets its week again);
    after that the interval grows with how well you knew it. */
export function grade(p: HifzPage, g: Grade, today: string): HifzPage {
  const base = { ...p, reviews: p.reviews + 1, last: { day: today, grade: g }, updatedAt: Date.now() };
  if (g === 'forgot') {
    // Back to daily revision for a week; when it graduates it starts with a short interval.
    return { ...base, lapses: p.lapses + 1, ease: Math.max(MIN_EASE, p.ease - 0.2), interval: 1, recentUntil: addDays(today, RECENT_DAYS - 1), due: addDays(today, RECENT_DAYS) };
  }
  if (isRecent(p, today)) {
    return { ...base, ease: g === 'shaky' ? Math.max(MIN_EASE, p.ease - 0.1) : p.ease };
  }
  const ease = g === 'strong' ? Math.min(MAX_EASE, p.ease + 0.05) : Math.max(MIN_EASE, p.ease - 0.15);
  const interval = Math.min(MAX_INTERVAL, Math.max(g === 'strong' ? 2 : 1, Math.round(p.interval * (g === 'strong' ? ease : 1.2))));
  return { ...base, ease, interval, due: addDays(today, interval) };
}

/** The memorised āyāt on a page, by surah, for showing it: [{ surah, from, to }]. */
export function pageParts(plan: Pick<HifzPlan, 'order' | 'known' | 'position' | 'sunnahFirst' | 'relearn'>, page: number): Lesson['parts'] {
  const mem = memorisedIds(plan);
  const [a, b] = pageIds(page);
  const parts: Lesson['parts'] = [];
  for (let id = a; id <= b; id++) {
    if (!mem.has(id)) continue;
    const s = surahOfId(id), ay = id - FIRST_ID[s] + 1, last = parts.at(-1);
    if (last && last.surah === s && last.to === ay - 1) last.to = ay;
    else parts.push({ surah: s, from: ay, to: ay });
  }
  return parts;
}

export const linesOf = (pages: number) => Math.max(1, Math.round(pages * 15));
export const GOAL_YEARS = [1, 2, 3, 4, 5] as const;
export const targetFor = (startDay: string, years: number) => {
  const [y, m, d] = startDay.split('-').map(Number);
  return new Date(Date.UTC(y + years, m - 1, d - 1)).toISOString().slice(0, 10);
};

/** Mark a saved lesson "not memorised": the latest one simply steps your place back; an older one is set aside
    to learn again (ahead of new lessons), so what you learnt after it stays memorised. */
export function unlearn(plan: HifzPlan, ids: readonly number[]): HifzPlan {
  const seq = planSequence(plan);
  const set = new Set(ids);
  let back = plan.position;
  while (back > 0 && set.has(seq[back - 1])) back--;
  const tail = plan.position - back;
  // Every id of the lesson at the end of what you've learnt → step back; otherwise, relearn them.
  if (tail === set.size) return { ...plan, position: back, relearn: (plan.relearn ?? []).filter((id) => !set.has(id)) };
  return { ...plan, relearn: [...new Set([...(plan.relearn ?? []), ...ids])].sort((a, b) => a - b) };
}
